import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Calendar, Clock, ChevronDown, Search, Loader2, PlusCircle, X, Plane, ArrowRightLeft, MessageCircle, Phone } from "lucide-react";
import { AddressAutocomplete } from "./AddressAutocomplete";
import { QuoteResults } from "./QuoteResults";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { AIRPORTS, HOUR_OPTIONS, fetchQuote, isWithin24hLondon } from "../../lib/booking";
import { waLink } from "../../lib/site";

const TABS = [
  { k: "one_way", label: "One Way" },
  { k: "airport", label: "Airport" },
  { k: "hourly", label: "Hourly" },
];

const SELECT =
  "w-full min-w-0 appearance-none rounded-xl border border-ink/10 bg-ink/[0.035] py-3 pl-9 pr-8 text-sm text-ink transition-colors focus:border-gold/70 focus:bg-white focus:outline-none";
const NATIVE =
  "w-full min-w-0 rounded-xl border border-ink/10 bg-ink/[0.035] py-3 pl-9 pr-3 text-sm text-ink transition-colors focus:border-gold/70 focus:bg-white focus:outline-none";

const toPoint = (s) =>
  s
    ? {
        description: s.description,
        lat: s.lat ?? null,
        lng: s.lng ?? null,
        place_id: s.place_id || null,
        airport_code: s.airport_code || null,
        is_airport: !!s.is_airport,
      }
    : null;

export const BookingWidget = () => {
  const today = new Date().toISOString().slice(0, 10);
  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);

  const [tab, setTab] = useState("one_way");
  const [pickup, setPickup] = useState(null);
  const [dropoff, setDropoff] = useState(null);
  const [airportCode, setAirportCode] = useState("LHR");
  const [direction, setDirection] = useState("from");
  const [airportOther, setAirportOther] = useState(null);
  const [hours, setHours] = useState(4);
  const [date, setDate] = useState(tomorrow);
  const [time, setTime] = useState("12:00");
  const [returnOn, setReturnOn] = useState(false);
  const [returnDate, setReturnDate] = useState(today);
  const [returnTime, setReturnTime] = useState("18:00");
  const [flight, setFlight] = useState("");
  const [loading, setLoading] = useState(false);

  const [results, setResults] = useState(null);
  const [quoteReq, setQuoteReq] = useState(null);
  const [open, setOpen] = useState(false);
  const [asapOpen, setAsapOpen] = useState(false);

  useEffect(() => {
    const l = window.__lenis;
    if (l) {
      if (asapOpen) l.stop();
      else l.start();
    }
  }, [asapOpen]);

  const showFlight = tab === "airport" || (tab === "one_way" && pickup?.is_airport);

  const build = () => {
    const datetime = `${date}T${time}`;
    const ret = returnOn ? `${returnDate}T${returnTime}` : null;

    if (tab === "hourly") {
      if (!pickup) {
        toast.error("Please enter a pick-up address.");
        return null;
      }
      return {
        service_type: "hourly",
        pickup: toPoint(pickup),
        dropoff: null,
        datetime,
        hours: Number(hours),
        return_trip: false,
        return_datetime: null,
        flight_number: null,
      };
    }

    if (tab === "airport") {
      if (!airportOther) {
        toast.error(direction === "from" ? "Please enter a drop-off address." : "Please enter a pick-up address.");
        return null;
      }
      if (!flight.trim()) {
        toast.error("Please enter the flight number.");
        return null;
      }
      const ap = AIRPORTS.find((a) => a.code === airportCode);
      const apPoint = { description: ap.name, airport_code: airportCode, is_airport: true, lat: null, lng: null, place_id: null };
      const pk = direction === "from" ? apPoint : toPoint(airportOther);
      const dp = direction === "from" ? toPoint(airportOther) : apPoint;
      return {
        service_type: "airport",
        pickup: pk,
        dropoff: dp,
        datetime,
        hours: null,
        return_trip: returnOn,
        return_datetime: ret,
        flight_number: flight.trim(),
      };
    }

    if (!pickup || !dropoff) {
      toast.error("Please enter both pick-up and drop-off addresses.");
      return null;
    }
    return {
      service_type: "one_way",
      pickup: toPoint(pickup),
      dropoff: toPoint(dropoff),
      datetime,
      hours: null,
      return_trip: returnOn,
      return_datetime: ret,
      flight_number: pickup?.is_airport ? flight.trim() || null : null,
    };
  };

  const search = async () => {
    if (isWithin24hLondon(date, time)) {
      setAsapOpen(true);
      return;
    }
    const payload = build();
    if (!payload) return;
    setLoading(true);
    try {
      const data = await fetchQuote(payload);
      setResults(data);
      setQuoteReq(payload);
      setOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not calculate a quote. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="relative w-full max-w-md overflow-hidden rounded-[28px] border border-white/50 bg-gradient-to-br from-white/85 via-white/70 to-white/60 p-2 shadow-[0_30px_80px_-22px_rgba(0,0,0,0.85)] ring-1 ring-white/25 backdrop-blur-[30px]"
      data-testid="booking-widget"
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-white/45 to-transparent" />
      {/* Tabs */}
      <div className="flex gap-1 rounded-[20px] bg-ink/[0.05] p-1" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.k}
            type="button"
            role="tab"
            onClick={() => setTab(t.k)}
            data-testid={`booking-tab-${t.k}`}
            aria-selected={tab === t.k}
            className={`flex-1 rounded-2xl px-2 py-2.5 text-[0.82rem] font-medium tracking-wide transition-all duration-300 ${
              tab === t.k ? "bg-white text-ink shadow-sm" : "text-ink hover:text-ink"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="space-y-4 p-3.5 sm:p-4">
        {tab === "one_way" && (
          <>
            <AddressAutocomplete
              label="Pickup Address"
              placeholder="Address, airport, hotel…"
              value={pickup}
              onSelect={setPickup}
              testid="booking-pickup"
            />
            <AddressAutocomplete
              label="Dropoff Address"
              placeholder="Address, airport, hotel…"
              value={dropoff}
              onSelect={setDropoff}
              testid="booking-dropoff"
            />
          </>
        )}

        {tab === "airport" && (
          <>
            <div className="flex items-center gap-1 rounded-xl bg-ink/[0.05] p-1" data-testid="booking-direction">
              <button
                type="button"
                onClick={() => setDirection("from")}
                data-testid="booking-direction-from"
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2 text-[0.78rem] font-medium transition-all ${
                  direction === "from" ? "bg-white text-ink shadow-sm" : "text-ink"
                }`}
              >
                <Plane size={13} /> From Airport
              </button>
              <button
                type="button"
                onClick={() => setDirection("to")}
                data-testid="booking-direction-to"
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2 text-[0.78rem] font-medium transition-all ${
                  direction === "to" ? "bg-white text-ink shadow-sm" : "text-ink"
                }`}
              >
                <ArrowRightLeft size={13} /> To Airport
              </button>
            </div>

            <div>
              <label className="mb-1.5 block text-[0.72rem] font-medium tracking-wide text-ink">Airport</label>
              <div className="relative">
                <Plane size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink/70" />
                <select
                  value={airportCode}
                  onChange={(e) => setAirportCode(e.target.value)}
                  data-testid="booking-airport-select"
                  className={SELECT}
                >
                  {AIRPORTS.map((a) => (
                    <option key={a.code} value={a.code}>
                      {a.name}
                    </option>
                  ))}
                </select>
                <ChevronDown size={15} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-ink/70" />
              </div>
            </div>

            <AddressAutocomplete
              label={direction === "from" ? "Dropoff Address" : "Pickup Address"}
              placeholder="Address, hotel, postcode…"
              value={airportOther}
              onSelect={setAirportOther}
              testid="booking-airport-other"
            />
          </>
        )}

        {tab === "hourly" && (
          <>
            <AddressAutocomplete
              label="Pickup Address"
              placeholder="Where should we collect you?"
              value={pickup}
              onSelect={setPickup}
              testid="booking-pickup"
            />
            <div>
              <label className="mb-1.5 block text-[0.72rem] font-medium tracking-wide text-ink">Duration</label>
              <div className="relative">
                <Clock size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink/70" />
                <select
                  value={hours}
                  onChange={(e) => setHours(Number(e.target.value))}
                  data-testid="booking-hours"
                  className={SELECT}
                >
                  {HOUR_OPTIONS.map((h) => (
                    <option key={h} value={h}>
                      {h} hours{h >= 8 ? " (day rate)" : ""}
                    </option>
                  ))}
                </select>
                <ChevronDown size={15} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-ink/70" />
              </div>
            </div>
          </>
        )}

        {showFlight && (
          <div>
            <label className="mb-1.5 block text-[0.72rem] font-medium tracking-wide text-ink">Flight Number</label>
            <div className="relative">
              <Plane size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink/70" />
              <input
                value={flight}
                onChange={(e) => setFlight(e.target.value)}
                placeholder="e.g. BA286"
                data-testid="booking-flight"
                className={NATIVE}
              />
            </div>
          </div>
        )}

        {/* Date & time */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-[0.72rem] font-medium tracking-wide text-ink">Select Date</label>
            <div className="relative">
              <Calendar size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink/70" />
              <input
                type="date"
                min={today}
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="booking-date"
                className={NATIVE}
              />
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-[0.72rem] font-medium tracking-wide text-ink">Select Time</label>
            <div className="relative">
              <Clock size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink/70" />
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                data-testid="booking-time"
                className={NATIVE}
              />
            </div>
          </div>
        </div>

        {tab !== "hourly" && (
          <div>
            {!returnOn ? (
              <button
                type="button"
                onClick={() => setReturnOn(true)}
                data-testid="booking-add-return"
                className="ml-auto flex items-center gap-2 text-[0.82rem] font-medium text-ink transition-colors hover:text-ink"
              >
                <PlusCircle size={16} /> Add Return Booking
              </button>
            ) : (
              <div className="rounded-xl border border-ink/10 bg-ink/[0.03] p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-[0.72rem] font-medium tracking-wide text-ink">Return Journey</span>
                  <button
                    type="button"
                    onClick={() => setReturnOn(false)}
                    data-testid="booking-remove-return"
                    className="flex items-center gap-1 text-[0.72rem] text-ink/80 hover:text-ink"
                  >
                    <X size={13} /> Remove
                  </button>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="relative">
                    <Calendar size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink/70" />
                    <input
                      type="date"
                      min={date}
                      value={returnDate}
                      onChange={(e) => setReturnDate(e.target.value)}
                      data-testid="booking-return-date"
                      className={NATIVE}
                    />
                  </div>
                  <div className="relative">
                    <Clock size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink/70" />
                    <input
                      type="time"
                      value={returnTime}
                      onChange={(e) => setReturnTime(e.target.value)}
                      data-testid="booking-return-time"
                      className={NATIVE}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={search}
          disabled={loading}
          data-testid="booking-search"
          className="flex w-full items-center justify-center gap-2 rounded-2xl bg-ink py-4 text-sm font-medium tracking-wide text-white transition-colors duration-300 hover:bg-gold hover:text-ink disabled:opacity-60"
        >
          {loading ? <Loader2 size={17} className="animate-spin" /> : <Search size={17} />}
          {loading ? "Calculating…" : "Search Ride"}
        </button>
      </div>

      <QuoteResults open={open} onOpenChange={setOpen} results={results} quoteReq={quoteReq} />

      <Dialog open={asapOpen} onOpenChange={setAsapOpen}>
        <DialogContent className="max-w-sm border-white/10 bg-charcoal text-white" data-testid="asap-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-light text-white">
              Booking within 24 hours
            </DialogTitle>
          </DialogHeader>
          <DialogDescription className="text-sm leading-relaxed text-white/70">
            Please call or message on WhatsApp for ASAP bookings. Thank you!
          </DialogDescription>
          <div className="mt-2 flex flex-col gap-2">
            <a
              href={waLink("Hello Upper Class Chauffeurs, I would like an ASAP booking (within 24 hours).")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="asap-whatsapp"
              className="flex items-center justify-center gap-2 rounded-lg bg-[#25D366] py-3 text-sm font-medium text-white transition-opacity hover:opacity-90"
            >
              <MessageCircle size={16} /> Message on WhatsApp
            </a>
            <a
              href="tel:+447539889920"
              data-testid="asap-call"
              className="flex items-center justify-center gap-2 rounded-lg border border-white/15 py-3 text-sm text-white transition-colors hover:border-gold/50"
            >
              <Phone size={16} /> Call +44 7539 889920
            </a>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
